import axios from "axios";
import { TrackingPixelsUpdateError } from "../types";
/**
 * Sub-module for line item tracking pixel operations.
 * Handles downloading bulk edit templates and updating creative flight pixels.
 */
export class TrackingPixels {
    constructor(sdk) {
        this.sdk = sdk;
    }
    /**
     * Downloads bulk edit tracking pixels template for given line items
     * @param lineItemIds Array of line item IDs
     * @returns Promise<ArrayBuffer> Excel template as array buffer
     */
    async downloadTemplate(lineItemIds) {
        return axios
            .post(`${this.sdk.urls.baseAPIUrl}/download/creativeTrackingPixelTemplate`, {
            data: {
                lineItemIds
            }
        }, {
            responseType: "arraybuffer"
        })
            .then((res) => res.data)
            .catch((error) => {
            // If response is arraybuffer but contains JSON error, parse it
            if (error.response?.data instanceof ArrayBuffer) {
                try {
                    const decoder = new TextDecoder();
                    const text = decoder.decode(error.response.data);
                    const errorData = JSON.parse(text);
                    // Attach parsed error to the axios error for better handling
                    error.response = {
                        ...error.response,
                        data: errorData
                    };
                    throw error;
                }
                catch (parseError) {
                    // If parsing fails, throw original error
                    throw error;
                }
            }
            throw error;
        });
    }
    /**
     * Updates tracking pixels for creative flights in a line item.
     * @param lineItemId Line item ID
     * @param creatives Array of creatives with their tracking pixels to update
     * @returns Promise resolving with the success response
     * @throws {TrackingPixelsUpdateError} On complete or partial failure
     */
    async update(lineItemId, creatives) {
        const payload = {
            creative_flights: creatives.map(({ id, trackingPixels }) => ({
                id,
                event_urls: trackingPixels
            }))
        };
        return axios
            .patch(`${this.sdk.urls.baseAPIUrl}/lineitem/${lineItemId}/creative-tracking-pixels`, payload, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then((res) => {
            // Invalidate the cached LineItem so subsequent fetches get fresh data
            this.sdk.campaigns.lineItems.reconcileCache("delete", { id: lineItemId });
            return res.data;
        })
            .catch((error) => {
            const errorData = error.response?.data;
            // Check if we have the expected error structure
            if (errorData?.status === "ERROR" && errorData.errors) {
                const { errors } = errorData;
                // Case 1: Complete failure - errors is string array
                if (Array.isArray(errors)) {
                    throw new TrackingPixelsUpdateError({
                        message: errors[0] || "Failed to update tracking pixels",
                        isPartialFailure: false
                    });
                }
                // Case 2: Partial failure - errors is object with creative IDs as keys
                if (typeof errors === "object") {
                    const failedCreatives = Object.entries(errors).map(([creativeId, error]) => ({
                        creativeId,
                        error: error
                    }));
                    throw new TrackingPixelsUpdateError({
                        message: `${failedCreatives.length} creative(s) failed to update`,
                        isPartialFailure: true,
                        failedCreatives
                    });
                }
            }
            // Fallback for unexpected error format
            throw new TrackingPixelsUpdateError({
                message: error.message || "Failed to update tracking pixels",
                isPartialFailure: false
            });
        });
    }
}
//# sourceMappingURL=index.js.map